import { useState } from 'react'
import { Search } from 'lucide-react'

// Cadastro de Rotas de Serviço (TCADROT do ERP) — Fretamento. Só o layout por enquanto: ainda não grava nem lê do banco.
// Campos e listas de opções vindos do print da tela do ERP; tamanhos e opções dos selects ainda precisam ser conferidos com a tabela.

interface Rota {
  codigo: string; descricao: string
  empresa: string; empresaNome: string; linha: string; linhaNome: string; grupo: string
  inicioLinha: string; horaInicial: string; finalLinha: string; horaFinal: string
  adicNoturno: 'Não' | 'Sim'; horasNoturnas: string; tempoPrecedente: string; tempoProcedente: string
  status: string; sentido: string; itinerario: string; onibusPadrao: string
  tipoRota: string; classeTrafego: string; veiculo: string; veiculoNome: string; tacografo: boolean
  kmsDeslocInicial: string; kmsDeslocFinal: string; percursoKms: string
  passageiros: string; imprimeEscala: 'Sim' | 'Não'; ultListaPass: string; freqRevisao: string
}

const NOVA: Rota = {
  codigo: '', descricao: '', empresa: '', empresaNome: '', linha: '', linhaNome: '', grupo: '',
  inicioLinha: '', horaInicial: '', finalLinha: '', horaFinal: '',
  adicNoturno: 'Não', horasNoturnas: '0,00', tempoPrecedente: '0,00', tempoProcedente: '0,00',
  status: 'Normal', sentido: 'Entrada', itinerario: '', onibusPadrao: '',
  tipoRota: 'Intermunicipal', classeTrafego: 'Bom', veiculo: '', veiculoNome: '', tacografo: false,
  kmsDeslocInicial: '', kmsDeslocFinal: '', percursoKms: '',
  passageiros: '', imprimeEscala: 'Sim', ultListaPass: '', freqRevisao: '',
}

const STATUS = ['Normal', 'Suspensa', 'Cancelada']
const SENTIDOS = ['Entrada', 'Saída', 'Entrada e saída']
const TIPOS_ROTA = ['Intermunicipal', 'Municipal', 'Interestadual']
const CLASSES_TRAFEGO = ['Bom', 'Regular', 'Ruim']
// botões laterais da tela do ERP (cada um abre uma tela filha da rota)
const BOTOES_FILHOS = ['Motoristas', 'Itinerário', 'Dias Rota', 'Tipo de Ônibus', 'Valores', 'Passageiros', 'Veículos', 'Link Mapa', 'Planilha Pass.', 'Vei. Cadastrado']

// Horas no formato do ERP: 5,45 = 5h45 (hora, vírgula, minutos)
const emMinutos = (s: string): number | null => {
  const m = /^(\d{1,2})[,.:](\d{1,2})$/.exec(s.trim())
  if (!m) return /^\d{1,2}$/.test(s.trim()) ? Number(s) * 60 : null
  return Number(m[1]) * 60 + Number(m[2].length === 1 ? m[2] + '0' : m[2])
}
const emHoras = (min: number) => `${Math.floor(min / 60)},${String(min % 60).padStart(2, '0')}`
const numero = (s: string) => Number(String(s).replace(/\./g, '').replace(',', '.')) || 0
const fmtKm = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
const somaDias = (iso: string, dias: number) => {
  const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + dias)
  return d.toLocaleDateString('pt-BR')
}

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={{ display: 'block', minHeight: '1.1rem', lineHeight: 1.2 }}>{label}</label>
      {children}
    </div>
  )
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8', borderRadius: '0.5rem', padding: '0.55rem 0.85rem 0.7rem', marginBottom: '0.6rem' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1a6ea8', marginBottom: '0.35rem' }}>{titulo}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem 0.9rem', alignItems: 'flex-end' }}>{children}</div>
    </div>
  )
}

// Campo de busca por código (ainda sem consulta): código + botão de lupa + descrição só de leitura
function Busca({ label, codigo, nome, onCodigo, larguraCodigo = 150, larguraNome = 240 }:
  { label: string; codigo: string; nome: string; onCodigo: (v: string) => void; larguraCodigo?: number; larguraNome?: number }) {
  return (
    <Campo label={label}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input type="text" value={codigo} style={{ width: larguraCodigo }} onChange={e => onCodigo(e.target.value)} />
        <button type="button" className="btn btn-secondary btn-sm" disabled title="Consulta ainda não ligada" style={{ padding: '0 0.5rem' }}><Search size={14} /></button>
        <input type="text" value={nome} readOnly tabIndex={-1} style={{ width: larguraNome, background: '#f3f4f6' }} />
      </div>
    </Campo>
  )
}

export default function CadastroRotasServico() {
  const [busca, setBusca] = useState('')
  const [form, setForm] = useState<Rota>(NOVA)
  const set = <K extends keyof Rota>(campo: K, valor: Rota[K]) => setForm(f => ({ ...f, [campo]: valor }))

  // campos calculados (iguais ao ERP): Tempo = hora final − inicial, Kms Total = soma dos três, P. Revisão = última lista + frequência
  const ini = emMinutos(form.horaInicial), fim = emMinutos(form.horaFinal)
  const tempo = ini != null && fim != null && fim >= ini ? emHoras(fim - ini) : ''
  const kmsTotal = numero(form.kmsDeslocInicial) + numero(form.kmsDeslocFinal) + numero(form.percursoKms)
  const proximaRevisao = form.ultListaPass && Number(form.freqRevisao) > 0 ? somaDias(form.ultListaPass, Number(form.freqRevisao)) : ''
  const somenteLeitura: React.CSSProperties = { background: '#f3f4f6', fontWeight: 700, color: '#1a6ea8' }

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      {/* Lista */}
      <div className="card" style={{ width: 300, flexShrink: 0 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.9rem', color: '#1a6ea8', paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff' }}>
          Rotas de Serviço
        </h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem', alignItems: 'center' }}>
          <div className="field" style={{ flex: 1 }}>
            <input type="text" placeholder="Código ou descrição..." value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setForm(NOVA)} title="Nova rota">+ Nova</button>
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhuma rota cadastrada.</p>
      </div>

      {/* Ficha da rota */}
      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8' }}>{form.codigo ? `${form.codigo} — ${form.descricao}` : 'Nova Rota de Serviço'}</h3>
          <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.25rem 0.7rem', borderRadius: 6, background: '#fff7ec', color: '#a66000', whiteSpace: 'nowrap' }}>
            Layout — a gravação será ligada na próxima etapa
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Secao titulo="Identificação">
              <Campo label="Código da Rota" width={190}>
                <input type="text" maxLength={20} value={form.codigo} onChange={e => set('codigo', e.target.value.toUpperCase())} />
              </Campo>
              <Campo label="Descrição da Rota" width={420}>
                <input type="text" maxLength={60} value={form.descricao} onChange={e => set('descricao', e.target.value.toUpperCase())} />
              </Campo>
              <Busca label="Empresa / Cliente" codigo={form.empresa} nome={form.empresaNome} onCodigo={v => set('empresa', v)} larguraCodigo={150} larguraNome={260} />
              <Busca label="Linha" codigo={form.linha} nome={form.linhaNome} onCodigo={v => set('linha', v)} larguraCodigo={70} larguraNome={150} />
              <Campo label="Grupo" width={80}>
                <input type="text" maxLength={3} value={form.grupo} onChange={e => set('grupo', e.target.value)} />
              </Campo>
            </Secao>

            <Secao titulo="Trajeto e horários">
              <Campo label="Início da Linha" width={240}>
                <input type="text" maxLength={30} value={form.inicioLinha} onChange={e => set('inicioLinha', e.target.value.toUpperCase())} />
              </Campo>
              <Campo label="Hora Inicial" width={90}>
                <input type="text" maxLength={5} placeholder="0,00" value={form.horaInicial} onChange={e => set('horaInicial', e.target.value.replace(/[^\d,.:]/g, ''))} />
              </Campo>
              <Campo label="Final da Linha" width={240}>
                <input type="text" maxLength={30} value={form.finalLinha} onChange={e => set('finalLinha', e.target.value.toUpperCase())} />
              </Campo>
              <Campo label="Hora Final" width={90}>
                <input type="text" maxLength={5} placeholder="0,00" value={form.horaFinal} onChange={e => set('horaFinal', e.target.value.replace(/[^\d,.:]/g, ''))} />
              </Campo>
              <Campo label="Tempo" width={90}>
                <input type="text" value={tempo} readOnly tabIndex={-1} style={somenteLeitura} title="Hora final menos hora inicial" />
              </Campo>
              <Campo label="Adic. Noturno" width={100}>
                <select value={form.adicNoturno} onChange={e => set('adicNoturno', e.target.value as Rota['adicNoturno'])}>
                  <option>Não</option><option>Sim</option>
                </select>
              </Campo>
              <Campo label="Horas noturnas" width={110}>
                <input type="text" maxLength={5} value={form.horasNoturnas} onChange={e => set('horasNoturnas', e.target.value.replace(/[^\d,.:]/g, ''))} />
              </Campo>
              <Campo label="Tempo precedente (hrs)" width={150}>
                <input type="text" maxLength={5} value={form.tempoPrecedente} onChange={e => set('tempoPrecedente', e.target.value.replace(/[^\d,.:]/g, ''))} />
              </Campo>
              <Campo label="Tempo procedente (hrs)" width={150}>
                <input type="text" maxLength={5} value={form.tempoProcedente} onChange={e => set('tempoProcedente', e.target.value.replace(/[^\d,.:]/g, ''))} />
              </Campo>
            </Secao>

            <Secao titulo="Classificação e veículo">
              <Campo label="Status da Rota" width={150}>
                <select value={form.status} onChange={e => set('status', e.target.value)}>{STATUS.map(s => <option key={s}>{s}</option>)}</select>
              </Campo>
              <Campo label="Sentido" width={150}>
                <select value={form.sentido} onChange={e => set('sentido', e.target.value)}>{SENTIDOS.map(s => <option key={s}>{s}</option>)}</select>
              </Campo>
              <Campo label="Cód. Itinerário" width={120}>
                <input type="text" maxLength={8} value={form.itinerario} onChange={e => set('itinerario', e.target.value)} />
              </Campo>
              <Campo label="Ônibus Padrão" width={120}>
                <input type="text" maxLength={8} value={form.onibusPadrao} onChange={e => set('onibusPadrao', e.target.value.toUpperCase())} />
              </Campo>
              <Campo label="Tipo de Rota" width={170}>
                <select value={form.tipoRota} onChange={e => set('tipoRota', e.target.value)}>{TIPOS_ROTA.map(s => <option key={s}>{s}</option>)}</select>
              </Campo>
              <Campo label="Classe Tráfego" width={130}>
                <select value={form.classeTrafego} onChange={e => set('classeTrafego', e.target.value)}>{CLASSES_TRAFEGO.map(s => <option key={s}>{s}</option>)}</select>
              </Campo>
              <Busca label="Veículo Contratado" codigo={form.veiculo} nome={form.veiculoNome} onCodigo={v => set('veiculo', v.toUpperCase())} larguraCodigo={90} larguraNome={220} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', paddingBottom: '0.45rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.tacografo} onChange={e => set('tacografo', e.target.checked)} style={{ width: 'auto' }} />
                Obrigatório tacógrafo?
              </label>
            </Secao>

            <Secao titulo="Quilometragem">
              <Campo label="Kms Desloc. Inicial" width={140}>
                <input type="text" inputMode="decimal" value={form.kmsDeslocInicial} onChange={e => set('kmsDeslocInicial', e.target.value.replace(/[^\d,.]/g, ''))} />
              </Campo>
              <Campo label="Kms Desloc. Final" width={140}>
                <input type="text" inputMode="decimal" value={form.kmsDeslocFinal} onChange={e => set('kmsDeslocFinal', e.target.value.replace(/[^\d,.]/g, ''))} />
              </Campo>
              <Campo label="Percurso em Kms" width={140}>
                <input type="text" inputMode="decimal" value={form.percursoKms} onChange={e => set('percursoKms', e.target.value.replace(/[^\d,.]/g, ''))} />
              </Campo>
              <Campo label="Kms Total" width={140}>
                <input type="text" value={kmsTotal ? fmtKm(kmsTotal) : ''} readOnly tabIndex={-1} style={somenteLeitura} title="Deslocamento inicial + final + percurso" />
              </Campo>
            </Secao>

            <Secao titulo="Passageiros e revisão">
              <Campo label="Passageiros" width={110}>
                <input type="text" inputMode="numeric" maxLength={4} value={form.passageiros} onChange={e => set('passageiros', e.target.value.replace(/\D/g, ''))} />
              </Campo>
              <Campo label="Imprime Escala" width={110}>
                <select value={form.imprimeEscala} onChange={e => set('imprimeEscala', e.target.value as Rota['imprimeEscala'])}>
                  <option>Sim</option><option>Não</option>
                </select>
              </Campo>
              <Campo label="Data últ. lista de passageiros" width={190}>
                <input type="date" value={form.ultListaPass} onChange={e => set('ultListaPass', e.target.value)} />
              </Campo>
              <Campo label="Freq. Revisão (dias)" width={140}>
                <input type="text" inputMode="numeric" maxLength={3} value={form.freqRevisao} onChange={e => set('freqRevisao', e.target.value.replace(/\D/g, ''))} />
              </Campo>
              <Campo label="Próxima revisão" width={140}>
                <input type="text" value={proximaRevisao} readOnly tabIndex={-1} style={{ ...somenteLeitura, color: '#b91c1c', textAlign: 'center' }} title="Data da última lista + frequência de revisão" />
              </Campo>
            </Secao>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary" disabled title="A gravação ainda não foi ligada">Incluir</button>
              <button className="btn btn-secondary" onClick={() => setForm(NOVA)}>Limpar</button>
            </div>
          </div>

          {/* Telas filhas da rota (botões laterais do ERP) */}
          <div style={{ width: 150, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b' }}>Dados da rota</div>
            {BOTOES_FILHOS.map(b => (
              <button key={b} className="btn btn-secondary btn-sm" disabled title="Em breve" style={{ justifyContent: 'center' }}>{b}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
